import { hslTripletToHex, contrastRatio, compositeOver } from './chunk-TZJXF4IU.js';

// src/theme/tokens.ts
var DESIGN_TOKENS_LIGHT = {
  background: "0 0% 100%",
  foreground: "240 10% 3.9%",
  card: "0 0% 100%",
  "card-foreground": "240 10% 3.9%",
  popover: "0 0% 100%",
  "popover-foreground": "240 10% 3.9%",
  // Primary IS the brand accent (`--primary: var(--brand)` in globals.css), so
  // primary buttons, links and focus rings follow tenant branding. These are
  // the default brand's values; ThemeProvider overrides them live, and canvas
  // code reads the live value through `resolveTokenColor`. They were left at
  // shadcn's near-black after the CSS moved to the brand, so every pre-hydration
  // canvas fallback drew primary/ring in the wrong colour.
  primary: "217 91% 43%",
  "primary-foreground": "0 0% 100%",
  secondary: "240 4.8% 95.9%",
  "secondary-foreground": "240 5.9% 10%",
  muted: "240 4.8% 95.9%",
  "muted-foreground": "240 3.8% 42%",
  accent: "240 4.8% 95.9%",
  "accent-foreground": "240 5.9% 10%",
  // Retuned to carry TEXT as well as a fill — see the `globals.css` :root
  // comment. Upstream's shadcn value was a button fill only and measured
  // 3.76:1 as the text colour ~150 call sites use it as.
  destructive: "0 74% 42%",
  "destructive-foreground": "0 0% 98%",
  border: "240 5.9% 90%",
  input: "240 3.8% 55%",
  ring: "217 91% 43%",
  "graph-selection": "270 75% 48%",
  // Tenant brand (overridable at runtime by ThemeProvider).
  brand: "217 91% 43%",
  "brand-foreground": "0 0% 100%",
  // Interface state (a notice, a partial result, a finished job, a hint) —
  // not the risk ramp. Text colour AND fill, so each has a `-foreground`.
  warning: "26 90% 31%",
  "warning-foreground": "0 0% 100%",
  success: "161 84% 23%",
  "success-foreground": "0 0% 100%",
  info: "201 90% 30%",
  "info-foreground": "0 0% 100%",
  // Direction of money, in and out — its own pair, not the risk ramp.
  "flow-in": "174 60% 27%",
  "flow-out": "28 88% 32%",
  // Risk buckets (escalation: safe -> severe). LIGHT values: darker than the
  // dark ones on purpose, because `text-risk-*` on a white card was 1.8-3.1:1.
  // The dark ramp is in DESIGN_TOKENS_DARK_OVERRIDES, which is new — these
  // four used to be theme-independent.
  "risk-none": "142 76% 25%",
  "risk-low": "84 81% 23%",
  "risk-medium": "38 92% 27%",
  "risk-high": "22 91% 34%",
  "risk-critical": "0 84% 40%",
  // Finality states. LIGHT values, split from the dark ones for the same reason
  // the risk ramp was: `text-finality-pending` measured 1.57:1 on a white card
  // (1.46 on its own chip fill) and not one of the four reached 4.5:1.
  "finality-pending": "47 96% 24%",
  "finality-confirmed": "84 81% 23%",
  "finality-finalized": "142 76% 25%",
  "finality-reorged": "0 84% 40%",
  // Deployment colour (blue/green). LIGHT values — same split, same reason
  // (`text-deploy-mixed` was 2.14:1). These were missing from this projection
  // entirely while `globals.css` had them, so `resolveTokenColor("--deploy-*")`
  // fell through to its `#888` placeholder whenever the DOM wasn't readable —
  // SSR and pre-hydration canvas, which is exactly what this file exists for.
  "deploy-blue": "217 91% 43%",
  "deploy-green": "142 76% 25%",
  "deploy-mixed": "38 92% 27%",
  // The starred / primary / pinned accent, the text on a SOLID status fill,
  // and the category scale (one hue per kind of thing). See globals.css.
  highlight: "38 92% 28%",
  "status-foreground": "0 0% 100%",
  "category-blue": "199 89% 31%",
  "category-violet": "262 83% 56%",
  "category-indigo": "239 84% 60%",
  "category-cyan": "189 94% 25%",
  "category-teal": "173 80% 24%",
  "category-emerald": "160 84% 24%",
  "category-amber": "38 92% 28%",
  "category-orange": "25 95% 33%",
  "category-rose": "350 89% 39%",
  "category-pink": "330 81% 40%"
};
var DESIGN_TOKENS_DARK_OVERRIDES = {
  background: "240 10% 3.9%",
  foreground: "0 0% 98%",
  card: "240 10% 3.9%",
  "card-foreground": "0 0% 98%",
  popover: "240 10% 3.9%",
  "popover-foreground": "0 0% 98%",
  // Still the brand in dark (globals.css re-declares `--primary: var(--brand)`).
  primary: "217 91% 65%",
  "primary-foreground": "0 0% 0%",
  secondary: "240 3.7% 15.9%",
  "secondary-foreground": "0 0% 98%",
  muted: "240 3.7% 15.9%",
  "muted-foreground": "240 5% 64.9%",
  accent: "240 3.7% 15.9%",
  "accent-foreground": "0 0% 98%",
  // A fill bright enough to read as text on the dark page needs DARK text on
  // it, so the pair moves together. See the `globals.css` .dark comment.
  destructive: "0 84% 70%",
  "destructive-foreground": "240 10% 4%",
  border: "240 3.7% 15.9%",
  input: "240 5% 48%",
  ring: "217 91% 65%",
  "graph-selection": "270 95% 78%",
  brand: "217 91% 65%",
  "brand-foreground": "0 0% 0%",
  // Bright enough to read as text on every dark panel, so the solid fill takes
  // dark text and the `-foreground` flips with it (as `destructive` does).
  warning: "43 96% 56%",
  "warning-foreground": "240 10% 4%",
  success: "158 64% 52%",
  "success-foreground": "240 10% 4%",
  info: "198 93% 60%",
  "info-foreground": "240 10% 4%",
  "flow-in": "170 48% 52%",
  "flow-out": "30 88% 58%",
  // The ramps the light block deliberately does not share. Each clears 4.5:1 on
  // the dark card AND the muted panels; the greens, reds and the blue were
  // brightened to get there once solid fills moved to `status-foreground`
  // (dark text here) — see the `globals.css` .dark comment.
  "risk-none": "142 76% 38%",
  "risk-low": "84 81% 44%",
  "risk-medium": "38 92% 50%",
  "risk-high": "22 91% 56%",
  "risk-critical": "0 84% 67%",
  "finality-pending": "47 96% 53%",
  "finality-confirmed": "84 81% 44%",
  "finality-finalized": "142 76% 38%",
  "finality-reorged": "0 84% 67%",
  "deploy-blue": "217 91% 65%",
  "deploy-green": "142 76% 38%",
  "deploy-mixed": "38 92% 50%",
  highlight: "38 92% 55%",
  "status-foreground": "240 10% 4%",
  "category-blue": "199 89% 55%",
  "category-violet": "262 83% 72%",
  "category-indigo": "239 84% 74%",
  "category-cyan": "189 94% 55%",
  "category-teal": "173 80% 55%",
  "category-emerald": "160 84% 55%",
  "category-amber": "38 92% 55%",
  "category-orange": "25 95% 55%",
  "category-rose": "350 89% 66%",
  "category-pink": "330 81% 65%"
};
var DESIGN_TOKENS_DARK = {
  ...DESIGN_TOKENS_LIGHT,
  ...DESIGN_TOKENS_DARK_OVERRIDES
};
var DESIGN_TOKENS_BASE = {
  radius: "0.5rem",
  "font-sans": '"Inter Variable", "Inter", system-ui, sans-serif',
  "font-mono": '"JetBrains Mono Variable", "JetBrains Mono", ui-monospace, monospace'
};
function normalizeName(name) {
  return name.startsWith("--") ? name.slice(2) : name;
}
function defaultTokenTriplet(name, mode = "light") {
  const key = normalizeName(name);
  const set = mode === "dark" ? DESIGN_TOKENS_DARK : DESIGN_TOKENS_LIGHT;
  return set[key];
}
function defaultTokenColor(name, mode = "light") {
  const triplet = defaultTokenTriplet(name, mode);
  return triplet ? `hsl(${triplet})` : void 0;
}
function tokenVar(name) {
  return `hsl(var(--${normalizeName(name)}))`;
}

// src/theme/palette.ts
var CHART_PALETTE = [
  "blue",
  "violet",
  "emerald",
  "amber",
  "rose",
  "cyan",
  "indigo",
  "fuchsia",
  "lime",
  "slate"
];
var RISK_PALETTE = {
  safe: "emerald",
  low: "lime",
  medium: "amber",
  high: "orange",
  severe: "red"
};
var FINALITY_PALETTE = {
  unknown: "slate",
  mempool: "violet",
  pending: "amber",
  observed: "cyan",
  confirmed: "blue",
  finalized: "emerald",
  reorged: "rose"
};

// src/theme/portals.ts
var PORTAL_THEMES = {
  // Customer dashboard keeps the default brand blue (tenant override may win).
  dashboard: { label: "Dashboard", brand: "221 83% 53%", brandForeground: "210 40% 98%" },
  // Admin: authoritative rose/red so destructive ops read as "you are in admin".
  "admin-portal": { label: "Admin", brand: "347 77% 50%", brandForeground: "0 0% 100%" },
  // QA: amber.
  "qa-portal": { label: "QA", brand: "38 92% 50%", brandForeground: "240 10% 12%" },
  // Analytics: violet.
  "analytics-portal": { label: "Analytics", brand: "262 83% 58%", brandForeground: "0 0% 100%" },
  // Self-service: teal.
  "selfservice-portal": { label: "Self-service", brand: "173 80% 40%", brandForeground: "0 0% 100%" }
};
function portalBrand(slug) {
  if (!slug) return void 0;
  const theme = PORTAL_THEMES[slug];
  if (!theme) return void 0;
  const { brand, brandForeground, fontSans } = theme;
  return sanitizeBrandTokens({ brand, brandForeground, fontSans });
}

// src/theme/index.ts
var DEFAULT_BRAND_IDENTITY = { name: "Authe", mark: "A", faviconUrl: "/favicon.ico" };
function brandAssetUrl(value) {
  if (typeof value !== "string" || !value || value.length > 2048 || /[\\\s\u0000-\u001f]/.test(value)) return void 0;
  if (value.startsWith("/") && !value.startsWith("//")) return value;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password ? url.href : void 0;
  } catch {
    return void 0;
  }
}
var HSL_RE = /^\s*(\d{1,3})\s+(\d{1,3})%\s+(\d{1,3})%\s*$/;
var FONT_RE = /^[\w\s,'"-]{1,200}$/;
var clamp = (n, min, max) => Math.min(max, Math.max(min, n));
function parseHslTriplet(value) {
  if (typeof value !== "string" || !value) return null;
  const m = HSL_RE.exec(value);
  if (!m) return null;
  const h = Number(m[1]);
  const s = Number(m[2]);
  const l = Number(m[3]);
  if (h > 360 || s > 100 || l > 100) return null;
  return { h, s, l };
}
function isHslTriplet(value) {
  return parseHslTriplet(value) !== null;
}
function formatHsl({ h, s, l }) {
  return `${h} ${s}% ${l}%`;
}
function brandForegroundFor(brand) {
  const hex = hslTripletToHex(brand);
  if (!hex) return "0 0% 100%";
  return contrastRatio(hex, "#000000") >= contrastRatio(hex, "#ffffff") ? "0 0% 0%" : "0 0% 100%";
}
function sanitizeBrandTokens(input, options = {}) {
  const {
    minLightness = 25,
    maxLightness = 72,
    minSaturation = 20,
    maxSaturation = 96
  } = options;
  const out = {};
  if (!input || typeof input !== "object") return out;
  for (const key of ["name", "tagline", "mark"]) {
    const value = input[key];
    const limit = key === "mark" ? 4 : key === "name" ? 80 : 160;
    if (typeof value === "string" && value.trim() && value.length <= limit && !/[\u0000-\u001f]/.test(value)) out[key] = value.trim();
  }
  for (const key of ["logoUrl", "logoDarkUrl", "faviconUrl"]) {
    const value = brandAssetUrl(input[key]);
    if (value) out[key] = value;
  }
  const brand = parseHslTriplet(input.brand);
  if (brand) {
    const clamped = {
      h: clamp(brand.h, 0, 360),
      s: clamp(brand.s, minSaturation, maxSaturation),
      l: clamp(brand.l, minLightness, maxLightness)
    };
    out.brand = formatHsl(clamped);
    out.brandForeground = isHslTriplet(input.brandForeground) && contrastRatio(hslTripletToHex(out.brand), hslTripletToHex(input.brandForeground)) >= 4.5 ? input.brandForeground : brandForegroundFor(out.brand);
  } else if (isHslTriplet(input.brandForeground)) {
    out.brandForeground = input.brandForeground;
  }
  if (typeof input.fontSans === "string" && FONT_RE.test(input.fontSans)) {
    out.fontSans = input.fontSans;
  }
  return out;
}
function resolveThemeBrand(input, mode) {
  const tokens = mode === "dark" ? DESIGN_TOKENS_DARK : DESIGN_TOKENS_LIGHT;
  const clean = sanitizeBrandTokens(input);
  const parsed = parseHslTriplet(clean.brand ?? tokens.brand);
  const surfaces = [tokens.background, tokens.card, tokens.muted].map((value) => hslTripletToHex(value));
  const step = mode === "dark" ? 1 : -1;
  let brand = formatHsl(parsed);
  for (let lightness = parsed.l; lightness >= 0 && lightness <= 100; lightness += step) {
    brand = formatHsl({ ...parsed, l: lightness });
    const hex = hslTripletToHex(brand);
    if (surfaces.every((surface) => contrastRatio(hex, compositeOver(hex, surface, 0.15)) >= 4.5)) break;
  }
  const fill = hslTripletToHex(brand);
  const fills = [fill, ...surfaces.map((surface) => compositeOver(fill, surface, 0.9))];
  const readable = (triplet) => fills.every((bg) => contrastRatio(bg, hslTripletToHex(triplet)) >= 4.5);
  const preferred = clean.brandForeground ?? tokens["brand-foreground"];
  const brandForeground = readable(preferred) ? preferred : brandForegroundFor(brand);
  return { ...clean, brand, brandForeground };
}

export { CHART_PALETTE, DEFAULT_BRAND_IDENTITY, DESIGN_TOKENS_BASE, DESIGN_TOKENS_DARK, DESIGN_TOKENS_DARK_OVERRIDES, DESIGN_TOKENS_LIGHT, FINALITY_PALETTE, PORTAL_THEMES, RISK_PALETTE, brandAssetUrl, brandForegroundFor, defaultTokenColor, defaultTokenTriplet, isHslTriplet, parseHslTriplet, portalBrand, resolveThemeBrand, sanitizeBrandTokens, tokenVar };
//# sourceMappingURL=chunk-3QJC4MIA.js.map
//# sourceMappingURL=chunk-3QJC4MIA.js.map